// Top-level React error boundary + a window.onerror hook. When a JS
// exception escapes the tree we POST it to /api/client-errors so the
// operator sees frontend crashes in the same JSON log stream as backend
// errors. Fails silently if the network is unreachable.
import React from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Cheap dedupe — same message reported multiple times in the same second
// only fires one POST. Prevents infinite loops when the error handler
// itself throws (e.g. rendering the fallback).
let __recent = new Map();
const shouldReport = (key) => {
  const now = Date.now();
  const last = __recent.get(key) || 0;
  if (now - last < 5000) return false;
  __recent.set(key, now);
  // trim
  if (__recent.size > 100) __recent = new Map([...__recent].slice(-50));
  return true;
};

async function reportClientError(payload) {
  try {
    const key = `${payload.source || ''}|${payload.message || ''}|${payload.line || ''}`;
    if (!shouldReport(key)) return;
    // Attach bearer token if we have one (best effort).
    const token = localStorage.getItem('token');
    await axios.post(`${BACKEND_URL}/api/client-errors`, payload, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      timeout: 5000,
    });
  } catch {
    // Never let an error handler throw.
  }
}

// Wire window-level handlers exactly once per document.
if (typeof window !== 'undefined' && !window.__ag_error_hooks__) {
  window.__ag_error_hooks__ = true;

  window.addEventListener('error', (ev) => {
    reportClientError({
      message: ev.message?.slice(0, 2000) || 'window.onerror',
      source: ev.filename?.slice(0, 500),
      line: ev.lineno,
      column: ev.colno,
      stack: ev.error?.stack?.slice(0, 8000),
      page_url: window.location.href.slice(0, 2000),
      user_agent: window.navigator.userAgent.slice(0, 500),
      extra: { handler: 'window.onerror' },
    });
  });

  window.addEventListener('unhandledrejection', (ev) => {
    const reason = ev.reason;
    reportClientError({
      message: (reason?.message || String(reason || 'unhandledrejection')).slice(0, 2000),
      stack: reason?.stack?.slice(0, 8000),
      page_url: window.location.href.slice(0, 2000),
      user_agent: window.navigator.userAgent.slice(0, 500),
      extra: { handler: 'unhandledrejection' },
    });
  });
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    reportClientError({
      message: (error?.message || 'React error boundary').slice(0, 2000),
      stack: (info?.componentStack || error?.stack || '').slice(0, 8000),
      page_url: typeof window !== 'undefined' ? window.location.href.slice(0, 2000) : null,
      user_agent: typeof window !== 'undefined' ? window.navigator.userAgent.slice(0, 500) : null,
      extra: { handler: 'react.ErrorBoundary' },
    });
  }

  handleReload = () => {
    // Attempt a soft recovery first.
    this.setState({ error: null });
    if (typeof window !== 'undefined') window.location.reload();
  };

  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen bg-slate-950 text-slate-200 flex items-center justify-center p-6">
          <div className="max-w-md text-center">
            <div className="w-14 h-14 rounded-full bg-red-950/50 border border-red-800 mx-auto mb-4 flex items-center justify-center">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                   strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                   className="text-red-400">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8"  x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <h1 className="text-white text-2xl font-semibold mb-2">Something went wrong</h1>
            <p className="text-slate-400 text-sm mb-6">
              The error has been captured. If it keeps happening, use the Support widget
              (bottom-right) with a quick description of what you were doing.
            </p>
            <button
              onClick={this.handleReload}
              className="px-4 py-2 rounded bg-cyan-600 hover:bg-cyan-500 text-white text-sm"
              data-testid="error-boundary-reload"
            >
              Reload the page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
